import { Icon } from "@iconify/react";
import { useFormikContext } from "formik";
import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import {
  Input,
  Label,
  Required,
  TextArea,
} from "../../components/shared/InputField";
import { Select } from "../../components/shared/Select";
import {
  Box,
  BoxContainer,
  Error,
  HStack,
  VStack,
} from "../../components/utils";
import { currencyDropDownValues } from "../../constants/DropDownValues";
import { CompanyDetailsPostModel } from "../../types/OnBoardDataModel";
import { Headings } from "../../components/Headings";
import {
  ButtonSize,
  IconCTAButton,
  PrimaryCTAButton,
} from "../quickRound/CTAButtonComponents";
import { useAuthStore } from "../../store";
import { queryClient } from "../../queries/client";
import { downloadS3File } from "../../utils/DownloadFile";

interface FileTypeModel {
  fileBlob: string;
  fileName?: string;
  fileType?: string;
}

type CompanyDetailFormDetail = {
  companyLogoImage?: string;
  setCompanyLogoImage: (fileDetail: FileTypeModel) => void;
};

export default function CompanyDetailForm({
  companyLogoImage,
  setCompanyLogoImage,
}: CompanyDetailFormDetail) {
  const formik = useFormikContext<CompanyDetailsPostModel>();
  const { errors, touched, getFieldProps, setFieldValue } = formik;
  const [excelUploading, setExcelUploading] = useState(false);

  // Get auth details from store
  const companyId = useAuthStore.getState().companyId || "";
  const authToken = useAuthStore.getState().accessToken || "";

  const isValidImageFileUploaded = (file: File) => {
    const validExtensions = ["png", "jpeg", "jpg"];
    const fileExtension = file.type.split("/")[1];
    return validExtensions.includes(fileExtension);
  };

  const handleEditProfileImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files?.[0]) {
      const file = e.target.files[0];
      if (isValidImageFileUploaded(file)) {
        setCompanyLogoImage({
          fileBlob: URL.createObjectURL(file),
          fileName: file.name,
          fileType: `image/${file.type.split("/")[1]}`,
        });
      } else {
        toast("File type not supported", { type: "error" });
      }
    }
  };

  const uploadAndFillExcel = async (file: File) => {
    try {
      setExcelUploading(true);

      // Validate file type
      const validExtensions = ["xlsx", "xlsm"];
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      if (!fileExtension || !validExtensions.includes(fileExtension)) {
        toast.error("Please upload a valid Excel file (.xlsx or .xlsm)");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      const baseUrl =
        process.env.REACT_APP_NEW_CAPTABLE_API || "http://localhost:4000";

      const response = await axios.put(
        `${baseUrl}v1/company/companyDetail/mcaMasterDataExcelUpload`,
        formData,
        {
          headers: {
            Authorization: authToken || "",
            companyId: companyId || "",
          },
        }
      );

      const data = response.status;

      if (data === 200) {
        toast.success("Company details uploaded and saved successfully!");
        queryClient.invalidateQueries("getCompanyDetails");
        queryClient.refetchQueries("getCompanyDetails");
        queryClient.invalidateQueries("userCompanies");
        queryClient.refetchQueries("userCompanies");
        queryClient.invalidateQueries("userContext");
        queryClient.refetchQueries("userContext");
        queryClient.invalidateQueries("getDirectorDetails");
        queryClient.refetchQueries("getDirectorDetails");
        queryClient.invalidateQueries("getInvDirectorDetails");
        queryClient.refetchQueries("getInvDirectorDetails");
      }
    } catch (err: any) {
      console.error("Upload error:", err);
      const msg =
        err?.response?.data?.errorMessage ||
        err?.response?.data?.message ||
        err?.response?.data?.reason ||
        "Failed to upload and save company details.";
      toast.error(msg);
    } finally {
      setExcelUploading(false);
    }
  };

  const handleExcelUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files?.[0]) {
      const file = e.target.files[0];
      uploadAndFillExcel(file);
      e.target.value = "";
    }
  };

  const downloadTemplate = () => {
    const templateUrl =
     "https://equion-dev.s3.us-west-2.amazonaws.com/captable-templates/Company+Details-template.xlsx";
    const link = document.createElement("a");
    link.href = templateUrl;
    link.download = "Company-Details-Template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fileInputRef = useRef(null);
  const excelInputRef = useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    const fileInput: any = fileInputRef.current;
    if (fileInput) fileInput.click();
  };

  const handleExcelUploadClick = () => {
    if (excelInputRef.current) excelInputRef.current.click();
  };

  return (
    <BoxContainer>
      <div className="flex justify-between items-center mb-4">
        <Headings text={"Company Details"} />
      </div>

      <VStack className="w-full gap-8">
        <HStack className="sm:gap-8 gap-4 sm:flex-row flex-col">
          <div className="flex-1">
            <Label>
              Name of the Company
              <Required />
            </Label>
            <Input
              type="text"
              placeholder="Enter Company Name"
              className=""
              {...getFieldProps("nameOfTheCompany")}
            />
            {touched.nameOfTheCompany && errors.nameOfTheCompany && (
              <Error text={errors.nameOfTheCompany} />
            )}
          </div>
          <div className="flex-1">
            <Label>
              CIN Number
              <Required />
            </Label>
            <Input
              type="text"
              placeholder="Eg:38928432"
              className=""
              {...getFieldProps("cinNumber")}
            />
            {touched.cinNumber && errors.cinNumber && (
              <Error text={errors.cinNumber} />
            )}
          </div>
        </HStack>

        <HStack className="sm:gap-8 gap-4 sm:flex-row flex-col">
          <div className="flex-1">
            <Label>
              Type of Company
              <Required />
            </Label>
            <Input
              type="text"
              placeholder="Enter Company Type"
              className=""
              {...getFieldProps("typeOfTheCompany")}
            />
            {touched.typeOfTheCompany && errors.typeOfTheCompany && (
              <Error text={errors.typeOfTheCompany} />
            )}
          </div>
          <div className="flex-1">
            <Label>
              Date of Incorporation
              <Required />
            </Label>
            <Input
              type="date"
              placeholder="Eg:12-12-1212"
              {...getFieldProps("dateOfIncorporation")}
            />
            {touched.dateOfIncorporation && errors.dateOfIncorporation && (
              <Error text={errors.dateOfIncorporation} />
            )}
          </div>
        </HStack>

        <HStack className="sm:gap-8 gap-4 sm:flex-row flex-col">
          <div className="flex-1">
            <Label>DIPP Cetificate Number</Label>
            <Input
              type="text"
              placeholder="Eg: UA30829832"
              {...getFieldProps("dippCertificateNumber")}
            />
            {touched.dippCertificateNumber && errors.dippCertificateNumber && (
              <Error text={errors.dippCertificateNumber} />
            )}
          </div>
          <div className="flex-1">
            <Label>
              Company Currency
              <Required />
            </Label>
            <Select
              options={currencyDropDownValues}
              {...getFieldProps("companyCurrency")}
            />
            {touched.companyCurrency && errors.companyCurrency && (
              <Error text={errors.companyCurrency} />
            )}
          </div>
        </HStack>

        <HStack className="sm:gap-8 gap-4 sm:flex-row flex-col">
          <div className="flex-1">
            <Label>
              Company Email ID
              <Required />
            </Label>
            <Input
              type="text"
              placeholder="Eg: example@domain.com"
              {...getFieldProps("companyEmailId")}
            />
            {touched.companyEmailId && errors.companyEmailId && (
              <Error text={errors.companyEmailId} />
            )}
          </div>
          <div className="flex-1">
            <Label>Company Website</Label>
            <Input
              type="text"
              placeholder="Eg: example.com"
              {...getFieldProps("companyWebsite")}
            />
            {touched.companyWebsite && errors.companyWebsite && (
              <Error text={errors.companyWebsite} />
            )}
          </div>
        </HStack>

        <HStack className="sm:gap-8 gap-4 sm:flex-row flex-col">
          <div className="flex-1">
            <Label>
              Corporate Office Address <Required />
            </Label>
            <TextArea
              placeholder="Enter Your Company Address"
              {...getFieldProps("corporateOfficeAddress")}
            />
          </div>
          <div className="flex flex-row justify-between flex-1 w-128">
            <Label>Profile Photo</Label>
            <Box className="w-30">
              {companyLogoImage === "" ? (
                <div>
                  <IconCTAButton
                    value={"Upload Image"}
                    onClick={handleUploadClick}
                    iconName={"material-symbols:upload-rounded"}
                    className={``}
                  />
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept="image/png, image/jpeg"
                    onChange={handleEditProfileImage}
                  />
                </div>
              ) : (
                <div className="relative flex flex-col justify-between">
                  <Label>
                    <input
                      type="file"
                      accept="image/png, image/jpg, image/jpeg"
                      className="hidden"
                      onInput={handleEditProfileImage}
                      {...getFieldProps("profileImageUrl")}
                    />
                    <Icon
                      icon="eva:edit-outline"
                      className="absolute z-10 w-6 h-6 text-gray-400 bg-gray-100 rounded-full cursor-pointer -right-2 hover:text-black"
                    />
                  </Label>
                  <img
                    className="right-0 block w-32 h-32 space-y-3 bg-gray-400 border-2 rounded-lg"
                    src={companyLogoImage}
                    alt="avatar"
                  />
                  <Label>
                    <Icon
                      icon="eva:close-fill"
                      onClick={() =>
                        setCompanyLogoImage({
                          fileBlob: "",
                          fileName: "",
                          fileType: "",
                        })
                      }
                      className="absolute z-10 w-6 h-6 text-gray-400 bg-gray-100 rounded-full cursor-pointer -right-2 -bottom-2 hover:text-black"
                    />
                  </Label>
                </div>
              )}
            </Box>
          </div>
        </HStack>

        {/* Excel Upload Section */}
        <div className="w-full flex items-center gap-4">
          <input
            ref={excelInputRef}
            type="file"
            accept=".xlsx,.xlsm"
            onChange={handleExcelUpload}
            className="hidden"
          />
          <VStack>
            <div className="pb-1">
              <PrimaryCTAButton
                loading={excelUploading}
                buttonSize={ButtonSize.MEDIUM}
                onClick={handleExcelUploadClick}
                disabled={excelUploading}
              >
                {excelUploading
                  ? "Uploading..."
                  : "Upload Excel for Company Details"}
              </PrimaryCTAButton>
            </div>
            <div className="">
              <p className="text-xs text-gray-400">
                download the excel template{" "}
                <span
                  onClick={() => {
                    downloadS3File("https://equion-dev.s3.us-west-2.amazonaws.com/captable-templates/Company+Details-template.xlsx", "Company-Details-Template.xlsx");
                  }}
                  className="underline text-blue-600 font-medium cursor-pointer hover:text-blue-800"
                >
                  here
                </span>
              </p>
            </div>
          </VStack>
        </div>
      </VStack>
    </BoxContainer>
  );
}
